import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatDialog as MatDialogMdc } from '@angular/material/dialog';
import { TwoFactorGateDialogComponent } from '../../components/v2-layout/shared/two-factor-gate-dialog/two-factor-gate-dialog.component';
import { TwoFactorGateReason } from '../../enums/two-factor-gate-reason.enum';
import { Observable, from, EMPTY, timeout } from 'rxjs';
import { switchMap, catchError, filter, take, finalize, tap } from 'rxjs/operators';
import { Routes } from '../../config/routes';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { TreasuryService } from './treasury.service';
import { UnifiedBankAccount } from '../../models/entities/unified-bank-account';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { BankAccountInsightsService } from '../financial-accounts/bank-account-insights.service';
import { GrowlerService } from '../../components/shared/growler/growler.service';
import { BankAccountsService } from '../financial-accounts/bank-accounts.service';
import {
    BankAccountKycDialogComponent,
    BankAccountKycDialogResult,
} from '../../components/app-layout/shared/bank-account-kyc-dialog/bank-account-kyc-dialog.component';
import {
    BankAccountStripeOnboardingDialogComponent,
    BankAccountStripeOnboardingDialogInput,
} from '../../components/app-layout/shared/bank-account-stripe-onboarding-dialog/bank-account-stripe-onboarding-dialog.component';
import { MatDialogSizes } from '../../enums/mat-dialog-sizes.enum';
import { ONBOARDING_STATUSES, TREASURY_VERIFICATION_BLOCKED_COPY } from '../../components/v2-layout/stripe-treasury/stripe-treasury.constants';
import { ConfirmActionV2DialogResult } from '../../components/v2-layout/shared/dialogs/confirm-action-dialog/confirm-action-dialog.component';
import { PrefillNoticeService } from './prefill-notice.service';
import { OrganizationBroadcastService, OrganizationEventType } from '../organization/organization-broadcast.service';
import {
    clampFcEventToken,
    clampFcInstitutionName,
    FC_ON_EVENT_BETA,
    FcAuthFlowEvent,
    FcAuthFlowEvents,
    FcSessionOutcome,
    FcSessionOutcomes,
    FinancialConnectionsTelemetryService,
} from './financial-connections-telemetry.service';

export interface FinancialConnectionsSessionResponse {
    clientSecret: string;
    sessionId: string;
    publishableKey: string;
    stripeAccount: string;
    /**
     * SD-9989: the session was opened to RE-AUTHORIZE the connection this bank account already
     * holds, rather than to start a fresh one. Core decides this — it resolves the target's `fca_`
     * and authorization itself — so the client only reports what it was given. Optional for a core
     * predating SD-9989.
     */
    isRelink?: boolean;
}

/**
 * SD-9989: the outcome of a relink session, read off the completed Stripe session.
 *
 * Only present when core opened the session with `relink_options`. `authorization` is set when the
 * customer re-authorized the connection we asked for; otherwise `failure_reason` says why not.
 *
 * Not declared by the bundled Stripe.js types (7.9.0 predates the relink preview), which is why
 * `collectAccounts()` still returns `any` and this shape is applied by hand at the read.
 */
export interface FcRelinkResult {
    authorization?: string | null;
    /**
     * The account that was relinked. Core always pins `relink_options.account`, so Stripe sets this
     * only when the customer brought back the SAME account we asked for — that is the whole point of
     * pinning it.
     */
    account?: string | null;
    failure_reason?: string | null;
}

/**
 * SD-10394. One event from Stripe's auth-flow events preview.
 *
 * Not declared by @stripe/stripe-js 7.9.0 — the runtime carries the preview, the types lag — so
 * this is applied by hand, the same way `FcRelinkResult` is.
 *
 * SD-10431 reads `institutionSelected.institutionName` and `success.manualEntry` into their OWN
 * fields on the payload. They are deliberately not folded into `code`, which means "Stripe's error
 * code" and nothing else — conflating two meanings in one column is the defect this work has
 * avoided throughout.
 */
export interface FcStripeAuthFlowEvent {
    type?: string;
    institutionSelected?: { institutionName?: string };
    success?: { manualEntry?: boolean };
    error?: { errorCode?: string };
}

/**
 * SD-10394. The collect call's options, plus the preview's `onEvent`.
 *
 * `CollectFinancialConnectionsAccountsOptions` declares `clientSecret` and nothing else in 7.9.0,
 * so `onEvent` is added here rather than cast away at the call. Structural typing carries the extra
 * property through, which keeps OUR object fully typed — deliberately not an `as` cast, because the
 * one thing that must stay type-checked is the sibling `betas` option on `loadStripe()`: that flag
 * is already typed, and it is the flag whose absence makes `onEvent` silently inert.
 */
interface FcCollectAccountsOptions {
    clientSecret: string;
    onEvent?: (event: FcStripeAuthFlowEvent) => void;
}

/**
 * SD-9732. What the browser observed about one popup, ready to report. Built by
 * `_observeSessionResult()`; the shape the telemetry request carries minus the session id and the
 * total duration, which the reporter adds.
 */
export interface FcObservedSessionOutcome {
    accountsReturned: number;
    reportedError: boolean;
    outcome: FcSessionOutcome;
    events: FcAuthFlowEvent[];
}

/** Stripe's `relink_result.failure_reason` values (SD-9989). */
export const FC_RELINK_FAILURE_NO_ACCOUNT = 'no_account';
export const FC_RELINK_FAILURE_NO_AUTHORIZATION = 'no_authorization';

/**
 * SD-10187. Core's `reason` when the connection the customer picked is not the bank account on the
 * row (last4, or routing when both sides carry one). Mirrors
 * `FinancialConnectionsAccountMismatchException::REASON`. The message on that response already
 * names both account endings, so callers render IT — never a paraphrase.
 */
export const FC_LINK_FAILURE_ACCOUNT_MISMATCH = 'fc_account_mismatch';

/**
 * SD-10187. Whether a failed link is core refusing a different bank account, as opposed to any
 * other error. Reads the body's `reason`, which `TreasuryException::render()` includes only when
 * set, so an older core (no field) falls through to the generic handling unchanged.
 */
export function isAccountMismatchError(error: unknown): error is HttpErrorResponse {
    // The message is part of the contract, not decoration: it is the only place both account endings
    // exist, and the dashboard renders it INSTEAD of the generic toast. A mismatch response with no
    // message must therefore fall through to the generic path (review F-9ae7) rather than render an
    // empty card and suppress the toast that would have said something.
    return error instanceof HttpErrorResponse
        && error.status === 422
        && error.error?.reason === FC_LINK_FAILURE_ACCOUNT_MISMATCH
        && typeof error.error?.message === 'string'
        && error.error.message.trim() !== '';
}

/**
 * Honest copy for a relink that did not restore the connection.
 *
 * Each reason gets its own sentence because each one asks the customer to do something different,
 * and the generic "nothing was linked" wording would send all three back to the same dead end. None
 * of them claims more than Stripe reported.
 */
export function fcRelinkFailureCopy(failureReason: string | null | undefined): string {
    switch (failureReason) {
        case FC_RELINK_FAILURE_NO_ACCOUNT:
            // They authenticated, but the account we asked for was not among the ones they shared.
            // Telling them to "try again" alone would repeat the same outcome, so name the choice.
            return 'You signed in, but the account we need was not selected. Reconnect and choose the '
                + 'same account you had linked before.';
        case FC_RELINK_FAILURE_NO_AUTHORIZATION:
            // The authorization itself could not be re-established — retrying immediately is the one
            // thing that tends to work here, since this is usually an interrupted sign-in.
            return 'Your bank did not finish re-authorizing this connection. Reconnect and complete '
                + 'the sign-in at your bank.';
        default:
            // Stripe reported a failure it did not classify. Say exactly that rather than inventing
            // a cause, and give the only instruction that is true under every unknown reading.
            return 'Your bank did not complete the reconnection. Nothing changed on your account — '
                + 'you can try again, or contact support if it keeps happening.';
    }
}

export interface ProcessAccountsRequest {
    accountIds: string[];
}

export interface ProcessedFcAccount {
    id: number;
    friendlyName: string;
    last4: string;
    routingNumber: string | null;
    sourceType: string;
    stripeFinancialConnectionsId: string;
    fcBalance: number | null;
}

export interface ProcessAccountsResponse {
    accounts: ProcessedFcAccount[];
}

/**
 * SD-9078. Details typed by the user after Stripe's Financial Connections modal declined to
 * return a linked account, which is what happens when they pick "enter details manually".
 */
export interface LinkManualAccountRequest {
    routingNumber: string;
    accountNumber: string;
    accountHolderType: 'company' | 'individual';
    /** Optional; the backend falls back to the organization's name for Stripe billing details. */
    accountHolderName?: string;
    friendlyName?: string;
}

/**
 * A Financial Connections result that completed with a session but linked nothing.
 *
 * SD-9078 review (spwoodall): there is NO way to tell a deliberate modal close from a manual-entry
 * completion. Stripe emits no cancellation signal for `collectFinancialConnectionsAccounts`:
 * `FinancialConnectionsSessionResult` is a two-arm union, `{financialConnectionsSession, error?:
 * undefined}` or `{financialConnectionsSession: undefined, error}` (stripe.d.ts:1344), with no
 * cancellation variant, and `FinancialConnectionsSession` itself carries only `id`, `accounts`,
 * `filters`, `permissions` and `return_url`: no status. `grep -r "session_cancelled"
 * node_modules/@stripe/stripe-js` returns zero hits on 7.9.0.
 *
 * An earlier revision of this file guarded on `error.code === 'session_cancelled'`. That code does
 * not exist, so the guard could never fire; it read as protection while providing none. Both a
 * close and a manual-entry completion arrive here as the FIRST arm with `accounts: []` and no
 * `error` at all, so this predicate names an AMBIGUOUS outcome, not a cancellation, and each caller
 * chooses a response that is honest under both readings.
 */
function completedWithNoAccounts(result: any): boolean {
    return !result?.error && (result?.financialConnectionsSession?.accounts ?? []).length === 0;
}

/**
 * The outcome of attaching a Financial Connections account to an existing bank account.
 *
 * SD-9824 added two fields beside `success`, which still means only "the link went through" and is
 * unchanged for existing readers. What it could never say is whether the account came out UN-FROZEN:
 * org 46726's admin got `success: true` and a balance that moved $647.58 -> $852.95, beside a
 * Reconnect prompt that stayed put.
 *
 * `connectionVerified: false` is NOT a failure and must not be shown as one — core refreshed the
 * balance but has not yet seen the succeeded TRANSACTION refresh its un-freeze requires.
 */
export interface LinkBalanceResult {
    /** The link itself went through: the FC account is stored and the balance refreshed. */
    success: boolean;
    /** The account is un-frozen — the Reconnect prompt is gone. Absent from a core predating SD-9824. */
    connectionVerified?: boolean;
    /** A background job is re-checking the connection and will clear the prompt if it recovered. */
    verificationPending?: boolean;
}

/** SD-10187. Core's answer to `inspectEmptySession`: did the customer type digits, or close the window? */
export interface FcSessionEntry {
    /** TRUE when the session carries a bank account token — the customer submitted a manual entry. */
    manualEntry: boolean;
    /** The typed account's last4 when Stripe exposes it, else null. */
    last4: string | null;
}

export interface LinkManualAccountResponse {
    account: {
        id: number;
        friendlyName: string;
        last4: string;
        routingNumber: string | null;
        sourceType: string;
        stripePaymentMethodId: string | null;
        /** Set when Stripe wants the two small deposits confirmed before it will move money. */
        microdepositVerificationRequiredAt: string | null;
    };
}

@Injectable({
    providedIn: 'root',
})
export class FinancialConnectionsService {
    /**
     * Safety cap on the link-balance request, applied to that request ONLY — never to the journey
     * that contains the Stripe popup (review finding F-8e40).
     *
     * Angular's HttpClient applies no default timeout and no interceptor supplies one, so a stalled
     * call never completes; the dashboard clears its in-flight flag on completion, so the row spun
     * with no route back to an actionable Reconnect short of a page refresh.
     *
     * Ninety seconds because everything it covers is machine-paced — core opens the session, reads
     * the account, reconciles the marker and queues the import; a measured run did the lot in about
     * 27 seconds. It is emphatically NOT sized for a human, which is exactly why it must not span
     * `collectFinancialConnectionsAccounts()`.
     */
    private static readonly LINK_BALANCE_REQUEST_TIMEOUT_MS = 90000;

    constructor(
        private _http: ApplicationHttpClient,
        private _treasuryService: TreasuryService,
        private _growler: GrowlerService,
        private _dialog: MatDialog,
        private _gateDialog: MatDialogMdc,
        private _bankAccountsService: BankAccountsService,
        private _orgBroadcast: OrganizationBroadcastService,
        private _bankAccountInsights: BankAccountInsightsService,
        private _fcTelemetry: FinancialConnectionsTelemetryService,
        private _prefillNotice: PrefillNoticeService,
        private _router: Router,
    ) {
        // SD-6865 broadcast adoption: replaces `_pollUntilTreasuryAccountReady`.
        // Backend dispatches `treasury.financial-account.ready` from
        // `TreasuryWebhookSubscriber::handleFinancialAccountFeaturesUpdated`
        // when outbound_transfers transitions to active (same trigger as the
        // payout-destination registration job). One fetch on receipt is all
        // that's needed — `createStream=true` pushes to `$bankAccounts`, so
        // subscribed deposit-account selectors flip to the readonly "PayHOA
        // Balance" state automatically.
        this._orgBroadcast.onEventType$(OrganizationEventType.TREASURY_FINANCIAL_ACCOUNT_READY)
            .pipe(
                // Defensive: Echo channels are already scoped to
                // `Organization.{orgId}`, but drop replayed or malformed
                // payloads that do not match the current JWT's orgId.
                filter((event) => LegFiJwtService.read()?.orgId === event.organizationId),
                switchMap((event) =>
                    this._bankAccountsService.getOrgBankAccounts(event.organizationId, null, true).pipe(take(1)),
                ),
            )
            .subscribe();
    }

    /**
     * Creates a Financial Connections session for linking external bank accounts.
     * Returns a clientSecret to initialize the Stripe Financial Connections modal.
     *
     * SD-9989: pass `relinkTargetBankAccountId` when the customer is RECONNECTING a bank account
     * rather than adding one. A session that names no existing objects can never reactivate a dead
     * connection — it can only mint a replacement — so a reconnect that omits the target is the
     * defect, not a variation. Only the local row id is sent: core resolves the Stripe objects from
     * the row itself, so the client can never nominate a connection it does not own.
     *
     * Sending a target does not force a relink. A row with no connection to re-authorize (the
     * SD-8605 first-time-link case) gets today's plain session back, which is why the reconnect
     * journey can pass the id unconditionally.
     */
    public createSession(
        organizationId: number,
        relinkTargetBankAccountId?: number,
    ): Observable<FinancialConnectionsSessionResponse> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.FinancialConnectionsSessions(organizationId),
        );
        const body = relinkTargetBankAccountId === undefined
            ? '{}'
            : JSON.stringify({ bankAccountId: relinkTargetBankAccountId });

        return this._http.post<FinancialConnectionsSessionResponse>(url, body);
    }

    /**
     * Processes linked accounts after the user completes the Stripe Financial Connections flow.
     * Sends the selected account IDs (fca_xxx format) to the backend for persistence.
     */
    public processAccounts(
        organizationId: number,
        accountIds: string[],
    ): Observable<ProcessAccountsResponse> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.FinancialConnectionsAccounts(organizationId),
        );
        const body: ProcessAccountsRequest = { accountIds };
        return this._http.post<ProcessAccountsResponse>(url, JSON.stringify(body));
    }

    /**
     * SD-9078: persist a bank account from routing/account numbers the user typed.
     *
     * The counterpart to processAccounts(). A Financial Connections session completed by manual
     * entry returns no `fca_` account, so there are no ids to post — the numbers themselves are
     * all we have, and the backend mints the Stripe PaymentMethod from them.
     */
    public linkManualAccount(
        organizationId: number,
        details: LinkManualAccountRequest,
    ): Observable<LinkManualAccountResponse> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.FinancialConnectionsManualAccounts(organizationId),
        );
        return this._http.post<LinkManualAccountResponse>(url, JSON.stringify(details));
    }

    /**
     * Get the Stripe-provided default friendly name for an FC account.
     */
    public getDefaultName(
        organizationId: number,
        fcAccountId: string,
    ): Observable<{ defaultName: string }> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.FinancialConnectionsAccountDefaultName(organizationId, fcAccountId),
        );
        return this._http.get<{ defaultName: string }>(url);
    }

    /**
     * Trigger a manual balance refresh and sync for an FC account.
     */
    public syncBalance(
        organizationId: number,
        fcAccountId: string,
    ): Observable<{ success: boolean }> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.FinancialConnectionsAccountSyncBalance(organizationId, fcAccountId),
        );
        return this._http.post<{ success: boolean }>(url, '{}');
    }

    /**
     * SD-10156: resolve a session that completed with zero linked accounts.
     *
     * The client payload is byte-identical for "the customer completed Stripe's manual-entry form"
     * and "the customer closed the window" — measured on a controlled pair. Only the SERVER can tell
     * them apart, by whether the session carries a bank account token, so the client sends the
     * session id and nothing else and acts on the answer:
     *
     *   `account` set  — the entry was completed; the account is registered and payable.
     *   `account` null — a genuine cancellation. Stay silent: nothing was collected, nothing to ask.
     *
     * The consent moment is the customer SUBMITTING their digits on Stripe's first screen. The
     * second screen is a Link-enrolment upsell, and declining it does not un-consent the entry —
     * measured, with the screenshot to prove it.
     */
    public resolveEmptySession(
        organizationId: number,
        sessionId: string,
    ): Observable<{ account: UnifiedBankAccount | null }> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.FinancialConnectionsSessionResolve(organizationId, sessionId),
        );

        return this._http.post<{ account: UnifiedBankAccount | null }>(url, '{}');
    }

    /**
     * SD-10187: what does a session that returned zero accounts contain? Read-only twin of
     * `resolveEmptySession` — the same server-side discriminator (a bank account token means the
     * customer TYPED digits; none means they closed the window), reported without acting on it.
     *
     * Used by the Link-account journey, where resolving would be wrong: the customer meant to attach
     * a feed to an existing row, not add a second one. All it needs to know is whether the SD-9731
     * "a balance needs a signed-in connection, not typed numbers" coaching applies to this person.
     */
    public inspectEmptySession(
        organizationId: number,
        sessionId: string,
    ): Observable<FcSessionEntry> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.FinancialConnectionsSessionInspect(organizationId, sessionId),
        );

        return this._http.get<FcSessionEntry>(url);
    }

    /**
     * SD-10394. The dynamic import, behind an overridable member so a test can see what is passed
     * to it — `card-visual.component.ts` exposes `_loadStripe` the same way and its spec spies it.
     *
     * Review finding F-fb3f: the `betas` flag below is the single switch that turns the auth-flow
     * events preview on, and an `onEvent` passed without it is SILENTLY inert — no error, no events.
     * With the call inlined there was no seam a spec could reach (every test in this file stubs
     * `collectAccounts` wholesale), so a dropped or misspelled flag would have left the suite green
     * and the feature collecting nothing. The import stays dynamic: stripe-js is heavy and this
     * keeps it out of the eager bundle.
     */
    protected _loadStripe = async (
        publishableKey: string,
        options: { stripeAccount: string; betas: string[] },
    ): Promise<any> => {
        const { loadStripe } = await import('@stripe/stripe-js');

        return loadStripe(publishableKey, options);
    };

    /**
     * Open the Stripe Financial Connections modal and collect accounts.
     * Loads Stripe.js dynamically and presents the FC modal to the user.
     *
     * SD-10394: `onEvent` is passed in rather than built here on purpose. This method is the seam
     * every spec in this file spies on, so a handler created INSIDE it could never be exercised by
     * a test — the same reason the SD-9732 timing lives in `_collectAccountsTimed()`. Crossing the
     * seam as a parameter is what makes the event collection testable at all.
     */
    public async collectAccounts(
        session: FinancialConnectionsSessionResponse,
        onEvent?: (event: FcStripeAuthFlowEvent) => void,
    ): Promise<any> {
        const stripe = await this._loadStripe(session.publishableKey, {
            stripeAccount: session.stripeAccount,
            // SD-10394: the beta belongs on the LOAD, not the collect call. An `onEvent` passed
            // without it is silently inert -- no error, no events -- so the two must not drift apart.
            betas: [FC_ON_EVENT_BETA],
        });

        if (!stripe) {
            throw new Error('Failed to load Stripe.js');
        }

        const options: FcCollectAccountsOptions = {
            clientSecret: session.clientSecret,
            onEvent,
        };

        return stripe.collectFinancialConnectionsAccounts(options);
    }

    /**
     * SD-9732. Run the Financial Connections popup and report how long it stayed open.
     *
     * Wraps collectAccounts() rather than instrumenting it, for two reasons: collectAccounts() has
     * no organization id (it takes only the session), and it is the seam every spec in this file
     * spies on — moving the timing inside it would put this measurement behind those spies and
     * quietly stop testing it.
     *
     * Timed with `performance.now()`, not `Date.now()`: it is monotonic, so an NTP correction or a
     * user changing the system clock mid-session cannot produce a negative or wildly inflated
     * duration. That matters more than usual here, because the number IS the evidence — a poisoned
     * duration does not just lose a data point, it lands in the cohort split as a false abandon.
     *
     * Reports on BOTH arms, including a rejected promise. A Stripe error is the one unambiguous
     * outcome we get, so recording it is what stops those sessions being counted as abandons; and a
     * throw here is exactly the case where the customer sees nothing and files the support ticket.
     * The report never alters the result: the resolved value is returned and a rejection is
     * rethrown, so every existing branch downstream behaves as before.
     */
    private async _collectAccountsTimed(
        organizationId: number,
        session: FinancialConnectionsSessionResponse,
    ): Promise<any> {
        const openedAt = performance.now();
        const collected: FcAuthFlowEvent[] = [];
        const onEvent = (event: FcStripeAuthFlowEvent): void => this._collectAuthFlowEvent(collected, event, openedAt);

        try {
            const result = await this.collectAccounts(session, onEvent);
            const observed = this._observeSessionResult(result, openedAt);

            this._reportSessionOutcome(organizationId, session, openedAt, {
                ...observed,
                // SD-10394: what Stripe said DURING the popup, then what we derive from its result.
                // Both are kept: an in-flow `error` and a result-level one can disagree, and which
                // of the two a session carries is itself the evidence.
                events: [...collected, ...observed.events],
            });

            return result;
        } catch (error) {
            this._reportSessionOutcome(organizationId, session, openedAt, {
                accountsReturned: 0,
                reportedError: true,
                outcome: FcSessionOutcomes.ERROR,
                events: [
                    ...collected,
                    { event: FcAuthFlowEvents.ERROR, code: null, atMs: this._elapsedMs(openedAt) },
                ],
            });

            throw error;
        }
    }

    /**
     * SD-10394. Record one auth-flow event. Never throws, and never records a nameless one.
     *
     * Stripe calls this from inside its own modal, so a throw here would escape into the customer's
     * bank link — telemetry is not allowed to break the journey it measures, the same rule
     * `_reportSessionOutcome()` follows and for the same reason.
     *
     * An event with no `type` is DROPPED rather than recorded blank: the server declares
     * `events.*.event` `required`, which rejects an empty string, and one such entry would 422 the
     * whole report and take the session's duration and outcome with it.
     */
    private _collectAuthFlowEvent(
        collected: FcAuthFlowEvent[],
        event: FcStripeAuthFlowEvent,
        openedAt: number,
    ): void {
        try {
            const name = clampFcEventToken(event?.type);

            if (!name) {
                return;
            }

            const institutionName = clampFcInstitutionName(event?.institutionSelected?.institutionName);
            const manualEntry = event?.success?.manualEntry;

            collected.push({
                event: name,
                code: clampFcEventToken(event?.error?.errorCode),
                atMs: this._elapsedMs(openedAt),
                // SD-10431: each rides only its own event type, so the key is omitted rather than
                // sent null when Stripe did not supply it.
                ...(institutionName !== undefined ? { institutionName } : {}),
                ...(typeof manualEntry === 'boolean' ? { manualEntry } : {}),
            });
        } catch (error) {
            console.error('Failed to record a Financial Connections auth-flow event:', error);
        }
    }

    /**
     * SD-9732. States how the popup ended in the client's own terms — facts, not verdicts.
     *
     * Read in order of how much Stripe actually SAID, most explicit first:
     *
     * 1. A returned `StripeError` — the popup itself failed; its `code` (falling back to `type`) is
     *    the first per-session "why" we have ever had.
     * 2. `relink_result.failure_reason` — a relink where Stripe stated the verdict (SD-9989). The
     *    customer may well have authenticated, so `accounts: []` here is a FAILURE, not an abandon,
     *    and it is keyed on `failure_reason`, never on a missing authorization (see the journey's
     *    own reading of this field). Review finding F-1038.
     * 3. `relink_result.account` — a relink Stripe confirmed. It routinely returns `accounts: []`
     *    because nothing NEW was linked, so counting the list would file a reconnect that worked
     *    under EMPTY — the exact bucket this report exists to keep honest. F-1038.
     * 4. The account list itself: anything returned is COMPLETED.
     * 5. Nothing at all — EMPTY, on purpose. With no cancellation signal a closed window and a
     *    completed manual entry are byte-identical here; the server settles which it was from the
     *    session object (SD-10156). Writing "cancelled" would launder a guess into the record; that
     *    value is reserved for Stripe's cancellation-as-error preview.
     */
    private _observeSessionResult(result: any, openedAt: number): FcObservedSessionOutcome {
        const accountsReturned: number = result?.financialConnectionsSession?.accounts?.length ?? 0;
        const error = result?.error;
        const relinkResult: FcRelinkResult | undefined = result?.financialConnectionsSession?.relink_result;
        const atMs = this._elapsedMs(openedAt);

        if (error) {
            return {
                accountsReturned,
                reportedError: true,
                outcome: FcSessionOutcomes.ERROR,
                events: [{ event: FcAuthFlowEvents.ERROR, code: error.code ?? error.type ?? null, atMs }],
            };
        }

        if (relinkResult?.failure_reason) {
            return {
                accountsReturned,
                reportedError: true,
                outcome: FcSessionOutcomes.ERROR,
                events: [{ event: FcAuthFlowEvents.RELINK_FAILED, code: relinkResult.failure_reason, atMs }],
            };
        }

        if (relinkResult?.account) {
            return {
                accountsReturned,
                reportedError: false,
                outcome: FcSessionOutcomes.COMPLETED,
                events: [{ event: FcAuthFlowEvents.RELINK_SUCCEEDED, code: null, atMs }],
            };
        }

        return {
            accountsReturned,
            reportedError: false,
            outcome: accountsReturned > 0 ? FcSessionOutcomes.COMPLETED : FcSessionOutcomes.EMPTY,
            events: [],
        };
    }

    /**
     * Rounded because the server validates an integer, and sub-millisecond precision is meaningless
     * against a signal whose cohorts are seconds apart from minutes.
     */
    private _elapsedMs(openedAt: number): number {
        return Math.round(performance.now() - openedAt);
    }

    /**
     * SD-9732. Emit one session-resolution report. Subscribes here because the caller is a promise
     * chain with nothing to compose the Observable into.
     *
     * TOTALLY non-throwing, and the try/catch is load-bearing rather than defensive garnish.
     * FinancialConnectionsTelemetryService swallows HTTP failures, but that covers only the
     * Observable's error channel: a SYNCHRONOUS throw — a missing injection, a stubbed spy that
     * returns undefined so `.subscribe()` is not a function — escapes it entirely. In
     * _collectAccountsTimed() such a throw would fall into the catch arm, report a SECOND time and
     * rethrow, so a broken diagnostic would both double-count the session and fail the customer's
     * bank link. Telemetry is not allowed to break the journey it measures.
     */
    private _reportSessionOutcome(
        organizationId: number,
        session: FinancialConnectionsSessionResponse,
        openedAt: number,
        observed: FcObservedSessionOutcome,
    ): void {
        try {
            this._fcTelemetry.reportSessionOutcome(organizationId, {
                sessionId: session.sessionId,
                durationMs: this._elapsedMs(openedAt),
                accountsReturned: observed.accountsReturned,
                reportedError: observed.reportedError,
                outcome: observed.outcome,
                events: observed.events,
            }).subscribe();
        } catch (error) {
            console.error('Failed to report Financial Connections session outcome:', error);
        }
    }

    /**
     * Link an FC account and sync balance for an external bank account created during KYC.
     * The fcAccountId comes from a completed Financial Connections session.
     */
    public linkExternalAccountBalance(
        organizationId: number,
        ubaId: number,
        fcAccountId: string,
    ): Observable<LinkBalanceResult> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.FinancialConnectionsLinkExternalBalance(organizationId, ubaId),
        );
        return this._http.post<LinkBalanceResult>(url, JSON.stringify({ fcAccountId }));
    }

    /**
     * SD-8605: the whole "Link account" journey for an EXISTING external bank account that has
     * no Financial Connections id — open the Stripe FC modal, then attach the chosen FC account
     * to that same UBA so a balance can be pulled.
     *
     * It never creates a bank account: the backend endpoint updates the UBA identified by
     * `ubaId` in place, so the user's manually-added routing/account record is preserved.
     *
     * Lives on the service (rather than in a component) because two surfaces need the identical
     * journey — the Treasury dashboard bank-balance card and the accounting Transactions
     * totals-box. Emits once on success; completes silently when the user dismisses the modal or
     * picks nothing, so callers can clear their own pending state in `complete`.
     */
    public linkBalanceForExistingAccount(
        organizationId: number,
        ubaId: number,
        callerRendersEmptyResultNotice = false,
        onExplainedFailure?: () => void,
    ): Observable<LinkBalanceResult> {
        // SD-9989: `ubaId` goes to the session create so core can open this as a RELINK of the
        // connection this row already holds. Passed unconditionally — a row with nothing to
        // re-authorize simply gets a plain session, which is this journey's original behavior.
        return this.createSession(organizationId, ubaId).pipe(
            switchMap((session) => from(this._collectAccountsTimed(organizationId, session))),
            switchMap((result) => {
                const accounts = result.financialConnectionsSession?.accounts ?? [];

                // A real Stripe error is the only case we can call a failure, so it is the only
                // case that gets an error toast.
                if (result.error) {
                    this._growler.error('Balance not linked', result.error.message);

                    return EMPTY;
                }

                // SD-9989. A relink session reports its own outcome, and that outcome is READ BEFORE
                // the ambiguous-empty branch below on purpose: a failed relink also arrives with
                // `accounts: []`, so leaving it to fall through would answer a question Stripe
                // actually answered with the copy written for the case where it said nothing.
                //
                // This is the one branch here that is not ambiguous. `completedWithNoAccounts` exists
                // because Stripe emits no cancellation signal, but `relink_result.failure_reason` is
                // an explicit verdict, so the copy can name the cause instead of hedging across four
                // readings.
                const relinkResult: FcRelinkResult | undefined = result.financialConnectionsSession?.relink_result;

                // Keyed on `failure_reason`, NOT on a missing `authorization`. A customer can
                // authenticate at their bank successfully and still fail the relink by picking a
                // different account than the one core pinned — Stripe's payments-or-payouts guide
                // describes exactly that, and leaves `relink_result.account` unset while the
                // authorization they just completed IS reported. Treating a present authorization as
                // success would call that outcome a win and then fall through to copy written for a
                // question Stripe already answered. `failure_reason` is the verdict itself, so it is
                // the thing to read.
                if (relinkResult?.failure_reason) {
                    this._growler.warning(
                        'Bank not reconnected',
                        fcRelinkFailureCopy(relinkResult.failure_reason),
                    );

                    // This outcome has now been EXPLAINED, which is a different thing from
                    // "nothing was linked" — and a caller rendering a persistent notice has no other
                    // way to tell the two apart, because both complete without emitting. Without it
                    // the Treasury dashboard adds its generic block underneath the message above,
                    // telling a customer who demonstrably DID sign in that a balance needs a
                    // signed-in connection, and pointing them at "Connect a new account" — the
                    // duplicate-minting action this ticket exists to remove. Review finding F-256d.
                    onExplainedFailure?.();

                    return EMPTY;
                }

                // SD-9078 review (spwoodall): "session present, accounts: [], no error" is
                // AMBIGUOUS: a deliberate close and a manual-entry completion are byte-identical
                // here (see completedWithNoAccounts). The previous revision showed an ERROR growler
                // for this shape, which meant anyone who simply clicked X was told something had
                // failed. That is the exact species of untrue message this ticket exists to remove,
                // and closing the modal is the most common way not to finish it.
                //
                // Unlike addBankAccount() there is no fallback to offer: this journey attaches a
                // BALANCE, and Stripe grants no balance data to a manually entered account. So the
                // response is a WARNING whose copy must be true under EVERY reading; it states only
                // that nothing was linked and what to do differently. It never asserts a failure
                // occurred.
                //
                // SD-9247 adds the THIRD reading, which the original copy did not cover: the
                // institution itself was unreachable. Org 46543's admin reached Primis Bank through
                // Fiserv AIS, which answered "Currently unable to access accounts at this
                // institution" on a page whose only control is Cancel. Cancelling there produces
                // this exact shape, so he was told to "log in to your bank in the Stripe window
                // instead" -- the one action that could not work, and the action he had already
                // taken six times. The institution case is NOT detectable (see
                // completedWithNoAccounts: Stripe emits no cancellation signal and no institution
                // status), so it cannot be branched on; the copy instead names it first and gives
                // the only useful instruction for it, which is to retry later.
                // SD-9557 shortened this. It was three sentences and too long to read in a growler
                // that dismisses itself, so the least useful sentence was dropped rather than the
                // wording being compressed.
                //
                // What went: the manual-entry clause ("if you entered your account numbers manually,
                // reopen this and sign in to your bank in the Stripe window instead"). Two reasons,
                // and the second matters more than the length. It assumed the reader arrived by
                // manual entry, which is not true of SD-9557's From-picker journey — they arrived
                // from a modal that offers only this route. And manual entry on a bare `ba_` mints a
                // PaymentMethod, which drops the row out of the payable path, and stamps a
                // microdeposit hold (proven on prod uba 61782, 2026-08-27). So the sentence pointed
                // some readers at the trap.
                //
                // UPDATED 2026-09-02 — the RECEIVE half of that is no longer true for rows linked
                // after SD-9569/SD-9564 (both 2026-08-28). Core now mints an outbound-only
                // SetupIntent first and stamps `stripe_outbound_setup_intent_id`, and
                // UnifiedBankAccount::getIsOutboundEligibleAttribute() no longer ANDs the hold out
                // when that marker is present — so such a row CAN receive. Rows linked BEFORE it
                // have a NULL marker and are still dead in both directions, which is the cohort the
                // paragraph above was measured on. `getIsTransferEligibleAttribute()` still ANDs the
                // hold out either way, so SENDING still waits on verification.
                //
                // The clause stays removed regardless: this journey is aimed at bare `ba_` rows, and
                // the send direction is what an admin here is usually after.
                //
                // What stays: the honest "nothing happened" statement, and SD-9247's institution
                // case — org 46543's admin hit an unreachable institution and retried six times, and
                // that is the whole reason this branch has copy at all.
                //
                // SD-9731 moved the retry coaching OUT of this growler and onto a persistent
                // alert-block beside the account (`linkFailureNotice`, v2 Treasury dashboard). The
                // constraint is the container, not the wording: a growler is hard-capped at a 5s
                // auto-dismiss (growler.service.ts, autoDismissAfter) with no per-call override,
                // and this branch has four distinct readings to serve.
                //
                // WHICH toast depends on the caller, and the default is the FULL one on purpose.
                // Only that dashboard renders the notice; the accounting totals-box and the SD-9557
                // transfer picker have no persistent surface, so for them this toast is the only
                // channel — including SD-9247's institution case. Trimming unconditionally would
                // delete that guarantee for the two callers that cannot replace it, hence opt-IN.
                //
                // Accepted loss: on the dashboard that institution sentence is not repeated on the
                // card, so it is absent from that one screen. Revisit by consolidating it INTO the
                // alert-block, not by re-expanding this toast.
                //
                // Evidence and decision history: SD-9731.
                // Stripe's own answer to "which account came back" beats inferring it from the
                // list. On a relink we pinned ONE account, and `relink_result.account` is Stripe
                // confirming that is the account re-authorized — so it is the id to attach.
                //
                // Two shapes make this more than tidiness. A completed relink can report the account
                // while returning `accounts: []`, because nothing NEW was linked — reading the list
                // would send that customer to the empty-result branch below and tell them nothing
                // happened, right after a reconnect that worked. And if the list ever carries more
                // than one entry, `accounts[0]` need not be the account we pinned, which would attach
                // the wrong bank account to this row. Review finding F-a21c.
                const relinkedAccountId = relinkResult?.account ?? null;

                if (!relinkedAccountId && completedWithNoAccounts(result)) {
                    // Two toasts, one fact. The retry sentence (SD-9247's institution-outage line) is
                    // trimmed ONLY when a persistent card beside the account will carry the coaching;
                    // a caller with no card always gets the full line.
                    const fullToast = 'Nothing was linked and your bank account is unchanged. If your bank '
                        + 'showed an error page, try again later.';
                    const trimmedToast = 'Nothing was linked and your bank account is unchanged.';

                    // SD-10187. Until now this branch could not tell "closed the window" from "typed
                    // digits into Stripe's manual-entry form" — SD-9731 chose neutral wording and
                    // showed its coaching card to both, because the card's advice (a balance needs a
                    // signed-in connection) is exactly right for the second person and only noise for
                    // the first. Core can now tell them apart by the session's bank account token,
                    // read-only.
                    //
                    // Only the caller that RENDERS the card asks (review F-9bdd): the totals-box and
                    // the transfer picker have no card to suppress, so for them the answer changes
                    // nothing and the request would be pure cost. They keep the full toast.
                    //
                    // Fail open to today's behaviour: if the probe errors, or Stripe's result carries
                    // no session id to ask about, nothing is known — and SD-9731's card is the
                    // pre-existing answer for "nothing is known".
                    const emptySessionId: string | undefined = result.financialConnectionsSession?.id;

                    if (!callerRendersEmptyResultNotice || !emptySessionId) {
                        this._growler.warning('No balance linked', callerRendersEmptyResultNotice ? trimmedToast : fullToast);

                        return EMPTY;
                    }

                    return this.inspectEmptySession(organizationId, emptySessionId).pipe(
                        tap((entry) => {
                            if (entry?.manualEntry === false) {
                                // No typed entry: a closed window OR an institution that returned
                                // nothing — this probe cannot tell those apart. The card is suppressed
                                // for both, so the toast must carry the retry advice itself (review
                                // F-e171): trimming it here left the outage case with no advice at all.
                                this._growler.warning('No balance linked', fullToast);
                                onExplainedFailure?.();

                                return;
                            }

                            this._growler.warning('No balance linked', trimmedToast);
                        }),
                        catchError(() => {
                            this._growler.warning('No balance linked', trimmedToast);

                            return EMPTY;
                        }),
                        switchMap(() => EMPTY),
                    );
                }

                // Bounded HERE, on the server call alone, and deliberately NOT on the outer pipe
                // (review finding F-8e40). The pipe above contains
                // `collectFinancialConnectionsAccounts()` — the Stripe popup — which is HUMAN-paced:
                // signing in at a bank, answering 2FA, choosing an account. A cap over the whole
                // journey would fire while the admin was still authenticating, discard a reconnect
                // they had successfully completed, and tell them to reload. That is worse than the
                // stuck spinner it was meant to prevent.
                //
                // Everything from this point is machine-paced, so a cap here is safe and is what
                // actually closes the hazard: `linkingAccountId` on the dashboard clears only when
                // this observable completes, and Angular's HttpClient applies no default timeout,
                // so a stalled request left the row spinning with no way back to an actionable
                // Reconnect. Note `timeout()` bounds time-to-first-emit from its UPSTREAM, so it
                // has to wrap this request rather than sit as an operator after the popup.
                return this.linkExternalAccountBalance(
                    organizationId,
                    ubaId,
                    relinkedAccountId ?? accounts[0].id,
                ).pipe(
                    timeout(FinancialConnectionsService.LINK_BALANCE_REQUEST_TIMEOUT_MS),
                );
            }),
        );
    }

    /**
     * Add a bank account — picks the right flow based on the Treasury feature flag
     * and the org's Connect verification status.
     *
     * Flow selection:
     * - Feature flag OFF → onStandardDialog; caller owns the legacy dialog. onComplete does NOT fire.
     * - Feature flag ON + Connect status 'verified' → opens Stripe Financial Connections modal.
     * - Feature flag ON + Connect status 'pending' / 'restricted' → explains why the action is
     *   unavailable and ends (SD-8351); these orgs have nothing to re-verify, so the KYC flow would
     *   only ask them to redo completed or unhelpful work.
     * - Feature flag ON + Connect status 'not_started' / 'incomplete' → opens the KYC dialog (v1),
     *   then the Stripe Connect onboarding dialog (v1) if the user elects to verify.
     *
     * Callback contract:
     * - onStandardDialog: fires only on the non-Treasury path; caller opens the legacy dialog.
     * - onFcSuccess: fires after a bank account is successfully linked (verified path only), by
     *   EITHER route — Financial Connections or the SD-9078 manual-entry recovery. It receives the
     *   id of the linked UnifiedBankAccount (the first, when Financial Connections returned
     *   several) so a caller can select the account the user just added; SD-8671 added the
     *   argument for the transfer page, which needs to drop the new account straight into the
     *   From/To picker the user was filling in. Callers that do not care may ignore it — a
     *   zero-argument callback still satisfies this signature.
     * - onComplete: fires once when the Treasury flow (FC or KYC) ends, regardless of outcome.
     *   Safe to reset loading state here. Does NOT fire on the onStandardDialog path.
     */
    public addBankAccount(
        organizationId: number,
        onStandardDialog: () => void,
        onFcSuccess: (linkedAccountId?: number) => void,
        onComplete: () => void,
    ): void {
        if (!this._bankAccountInsights.hasTreasuryEnabled()) {
            onStandardDialog();
            return;
        }

        this._treasuryService.connectStatus$.pipe(
            filter((status) => status !== null),
            take(1),
            switchMap((status) => {
                // SD-8351: an org that is PENDING has already completed KYC and is waiting on
                // Stripe's review, and a RESTRICTED org is told by the dashboard banner to contact
                // support. Re-opening the "Start Verification" flow for either one asks them to
                // redo work that is either already done or cannot help — so only the statuses that
                // genuinely have verification left to do (not_started / incomplete) reach the KYC
                // flow. Enforced HERE, at the shared seam, because the accounting Transactions
                // totals-box calls this same method (transactions.component.ts) and would
                // otherwise keep re-prompting even once the dashboard button is disabled.
                if (status === ONBOARDING_STATUSES.PENDING || status === ONBOARDING_STATUSES.RESTRICTED) {
                    const copy = TREASURY_VERIFICATION_BLOCKED_COPY[status];
                    this._growler.warning(copy.title, copy.message);
                    onComplete();

                    return EMPTY;
                }

                if (status !== ONBOARDING_STATUSES.VERIFIED) {
                    this._openKycFlow(organizationId, onComplete);
                    return EMPTY;
                }

                return this.createSession(organizationId).pipe(
                    // The result stage is NESTED rather than chained because it needs the
                    // session id: an empty result is resolved SERVER-side against that session
                    // (SD-10156), which is what retired the re-prompt dialog this pipe used to
                    // open. SD-9078's review note here — "the converse is not detectable either" —
                    // was true of the account list and is FALSE of the session object: a completed
                    // manual entry carries a bank account token, a closed window does not, measured
                    // on a controlled pair. The consent moment is the customer submitting their
                    // digits on Stripe's first screen; the second screen is a Link-enrolment
                    // upsell, and declining it does not un-consent the entry.
                    switchMap((session) => from(this._collectAccountsTimed(organizationId, session)).pipe(
                        switchMap((result) => {
                            if (result.error) {
                                this._growler.warning(
                                    'Bank connection failed',
                                    result.error.message || 'Something went wrong connecting to your bank. Please try again.',
                                );

                                return EMPTY;
                            }

                            if (completedWithNoAccounts(result)) {
                                return this.resolveEmptySession(organizationId, session.sessionId);
                            }

                            const accounts = result.financialConnectionsSession?.accounts ?? [];
                            const accountIds = accounts.map((account: any) => account.id);

                            return this.processAccounts(organizationId, accountIds);
                        }),
                    )),
                    catchError((error) => {
                        this._growler.fromErrorResponse(error);
                        return EMPTY;
                    }),
                    finalize(() => onComplete()),
                );
            }),
        ).subscribe({
            next: (result: any) => {
                // The OAuth path resolves with `accounts`; the manual path resolves with `account`
                // (or null for a cancellation, which is silent by design — nothing was collected).
                if (result?.accounts?.length) {
                    const count = result.accounts.length;
                    this._growler.success(
                        'Account linked',
                        count === 1
                            ? 'Bank account linked successfully.'
                            : `${count} bank accounts linked successfully.`,
                    );
                    onFcSuccess(result.accounts[0]?.id);

                    return;
                }

                if (result?.account) {
                    this._growler.success('Account added', 'Bank account added successfully. It can receive payouts right away.');
                    onFcSuccess(result.account.id);
                }
            },
        });
    }

    /**
     * Open the KYC / onboarding flow for a Treasury-enabled org that has not yet
     * completed verification. Always shows the v1 KYC intro dialog first, then —
     * on "Start Verification" — opens the Stripe Connect onboarding dialog. Fires
     * onComplete when the flow ends.
     */
    private _openKycFlow(organizationId: number, onComplete: () => void): void {
        this._dialog.open(BankAccountKycDialogComponent, {
            panelClass: 'legacy-kyc-dialog',
            width: MatDialogSizes.MD,
            maxWidth: MatDialogSizes.MD,
            disableClose: true,
            autoFocus: true,
        }).afterClosed().subscribe({
            next: (result: BankAccountKycDialogResult) => {
                if (result?.startVerification) {
                    this._openStripeOnboardingDialog(organizationId, onComplete);
                } else {
                    onComplete();
                }
            },
        });
    }

    private _openStripeOnboardingDialog(organizationId: number, onComplete: () => void): void {
        // SD-8102: gate mandatory-2FA users before v1 Connect onboarding (parity with
        // the v2 KYC interception). TREASURY_KYC is a soft gate — dismissing abandons setup.
        // SD-8330: keyed on mfaEnforced, not mfaRequired — pre-verification mfaRequired is false; this
        // admin-only path is where the enroll-before-KYC prompt belongs.
        if (LegFiJwtService.read()?.mfaEnforced) {
            TwoFactorGateDialogComponent.open(this._gateDialog, TwoFactorGateReason.TREASURY_KYC)
                .afterClosed()
                .pipe(take(1))
                .subscribe((enrolled) => {
                    if (enrolled === true) {
                        this._checkPrefillThenLaunch(organizationId, onComplete);
                    } else {
                        onComplete();
                    }
                });

            return;
        }

        this._checkPrefillThenLaunch(organizationId, onComplete);
    }

    /**
     * SD-9498: the v1 half of the pre-verification notice — same seam, same copy, same three-way
     * answer as the v2 dashboard, opened on the MDC dialog service because the notice is a v2
     * component. `app.scss`'s `.cdk-overlay-pane.v2-dialog` reset (SD-9047) is what makes it render
     * correctly from this legacy host.
     *
     * One seam covers every v1 entry into KYC — all ten `addBankAccount()` callers funnel through
     * `_openStripeOnboardingDialog` — including invoice creation, the highest-traffic way in.
     *
     * `onComplete()` fires on every exit that does not launch onboarding: it is the only thing that
     * clears the caller's in-flight flag (`bank-account-selector.component.ts:242`), and a path that
     * skips it leaves the Add-bank-account button dead until the page is reloaded.
     */
    private _checkPrefillThenLaunch(organizationId: number, onComplete: () => void): void {
        this._prefillNotice.confirm(organizationId, this._gateDialog).pipe(
            take(1),
        ).subscribe((result: ConfirmActionV2DialogResult) => {
            // Checked before `success`, which the secondary result also carries.
            if (result?.secondary) {
                // A NEW TAB, not this._router.navigate(): this seam is reachable from inside forms
                // the admin is mid-way through — bank-account-selector sits in add-transaction,
                // return-payment, mark-as-paid and payable-update — and Angular Material tears a
                // dialog down on navigation (closeOnNavigation defaults true), silently discarding
                // what they typed. The v2 copy navigates in-app because its host is the dashboard,
                // which has nothing to lose.
                const settingsUrl = this._router.serializeUrl(
                    this._router.createUrlTree(['/app/settings/organization']),
                );
                window.open(settingsUrl, '_blank');
                onComplete();

                return;
            }

            if (result?.success) {
                this._launchStripeOnboardingDialog(organizationId, onComplete);

                return;
            }

            // Backing out settles the caller the same way dismissing the 2FA gate does.
            onComplete();
        });
    }

    private _launchStripeOnboardingDialog(organizationId: number, onComplete: () => void): void {
        const data: BankAccountStripeOnboardingDialogInput = { organizationId };
        this._dialog.open(BankAccountStripeOnboardingDialogComponent, {
            width: BankAccountStripeOnboardingDialogComponent.width,
            maxWidth: BankAccountStripeOnboardingDialogComponent.width,
            disableClose: true,
            autoFocus: true,
            data,
        }).afterClosed().subscribe(() => {
            // Stripe's embedded onboarding has no success/cancel signal — `setOnExit`
            // fires either way. Post-KYC live updates are now broadcast-driven
            // (SD-6865 broadcast adoption):
            //   - `.treasury.connect-status.updated` flips `connectStatus$` to
            //     verified once `account.updated` lands with capability=active.
            //   - `.treasury.financial-account.ready` triggers the bank-accounts
            //     refresh once outbound_transfers features become active.
            // Both events are dispatched from `TreasuryWebhookSubscriber`, so no
            // local orchestration (refresh + poll) is needed here.
            onComplete();
        });
    }
}
